import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { apiService } from '../services/api';
import { useIBGatewayConnection, getConnectionStatusColors } from '../hooks/useIBGatewayConnection';
import { PieChart as RechartsPieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, LabelList } from 'recharts';
import { 
  ArrowLeft, 
  User, 
  Building2, 
  Activity, 
  TrendingUp, 
  CreditCard,
  Settings,
  PieChart,
  BarChart3,
  Shield,
  RefreshCw,
  Edit,
  Save,
  X,
  Plus,
  Trash2,
  ChevronDown,
  Check,
  Wifi,
  WifiOff,
  AlertCircle
} from 'lucide-react';

const UserAccountDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // Helper function to format currency with commas
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };
  const urlParams = new URLSearchParams(window.location.search);
  const returnTab = urlParams.get('returnTab');
  const [activeTab, setActiveTab] = useState('risk-profile');
  const [loading, setLoading] = useState(true);
  const [userAccount, setUserAccount] = useState(null);
  const [portfolios, setPortfolios] = useState([]);
  const [cashBalances, setCashBalances] = useState([]);
  const [riskProfile, setRiskProfile] = useState(null);
  const [riskAllocations, setRiskAllocations] = useState([]);
  const [trades, setTrades] = useState([]);
  const [instruments, setInstruments] = useState([]);

  // Mapping of ticker symbols to their underlying index names
  const tickerToIndexMap = {
    'SSO': 'S&P 500',
    'QLD': 'Nasdaq',
    'UWM': 'Russell 2000',
    'DDM': 'Dow Jones',
    'BTCL': 'Bitcoin',
    'ETHU': 'Ethereum',
    'ETHA': 'Ethereum',
    'BTC': 'Bitcoin',
    'ETH': 'Ethereum',
    'SPY': 'S&P 500',
    'QQQ': 'Nasdaq',
    'IWM': 'Russell 2000',
    'DIA': 'Dow Jones'
  };

  // Helper function to get the display name for an instrument
  const getInstrumentDisplayName = (instrument) => {
    if (instrument?.symbol?.label) {
      return instrument.symbol.label;
    }
    if (instrument?.ticker && tickerToIndexMap[instrument.ticker]) {
      return tickerToIndexMap[instrument.ticker];
    }
    return instrument?.ticker || 'Unknown';
  };
  
  // Risk Profile Creation Modal state
  const [showRiskProfileModal, setShowRiskProfileModal] = useState(false);
  const [editingRiskProfile, setEditingRiskProfile] = useState(null);
  const [riskProfileFormData, setRiskProfileFormData] = useState({
    name: '',
    min_cash_reserve_percent: 0,
    allocations: []
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState('');
  const [openDropdowns, setOpenDropdowns] = useState({});

  // Broker connection status
  const { status: connectionStatus, isChecking, lastChecked, refresh: refreshConnection, supportsBrokerAPI } = useIBGatewayConnection(userAccount);
  
  // Live portfolio data from broker API
  const [livePortfolioData, setLivePortfolioData] = useState(null);
  const [liveDataLoading, setLiveDataLoading] = useState(false);
  const [liveDataError, setLiveDataError] = useState(null);

  const tabs = [
    { id: 'risk-profile', label: 'Investment Strategy', icon: Shield },
    { id: 'portfolio', label: 'Portfolio', icon: PieChart },
    { id: 'transactions', label: 'Transactions', icon: Activity }
  ];

  useEffect(() => {
    if (id) {
      fetchUserAccountData();
    }
  }, [id]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.relative')) {
        setOpenDropdowns({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch live portfolio data from broker API
  const fetchLivePortfolioData = async () => {
    if (!userAccount || !supportsBrokerAPI || connectionStatus !== 'Connected') {
      return;
    }

    setLiveDataLoading(true);
    setLiveDataError(null);

    try {
      const response = await apiService.getPortfolioData({
        user_exchange_account_id: userAccount.id
      });

      if (response.data.status === 'success') {
        setLivePortfolioData(response.data.data);
      } else {
        setLiveDataError(response.data.message || 'Failed to fetch portfolio data');
      }
    } catch (error) {
      console.error('Error fetching live portfolio data:', error);
      setLiveDataError(
        error.response?.data?.message || 
        'Failed to fetch live portfolio data from broker API'
      );
    } finally {
      setLiveDataLoading(false);
    }
  };

  // Auto-fetch live data when connected and when tab becomes active
  useEffect(() => {
    if (connectionStatus === 'Connected' && supportsBrokerAPI && activeTab === 'portfolio') {
      fetchLivePortfolioData();
    }
  }, [connectionStatus, supportsBrokerAPI, activeTab]);

  const fetchUserAccountData = async () => {
    try {
      setLoading(true);
      
      // Fetch user exchange account details
      const accountRes = await apiService.getUserExchangeAccount(id);
      setUserAccount(accountRes.data);

      // Fetch related data in parallel
      const [portfoliosRes, cashBalancesRes, riskProfileRes, instrumentsRes] = await Promise.all([
        apiService.getPortfolios({ user_exchange_account: id }).catch(() => ({ data: { results: [] } })),
        apiService.getPortfolioCashBalances({ user_exchange_account: id }).catch(() => ({ data: { results: [] } })),
        // Fetch risk profile for this specific user + exchange combination
        apiService.getRiskProfiles({ 
          customuser: accountRes.data.customuser?.id, 
          exchange: accountRes.data.exchange?.id 
        }).catch(() => ({ data: { results: [] } })),
        // Fetch instruments for this exchange
        apiService.getInstruments({ exchange: accountRes.data.exchange?.id }).catch(() => ({ data: { results: [] } }))
      ]);

      setPortfolios(portfoliosRes.data.results || portfoliosRes.data || []);
      setCashBalances(cashBalancesRes.data.results || cashBalancesRes.data || []);
      setInstruments(instrumentsRes.data.results || instrumentsRes.data || []);
      setTrades([]); // No trades yet - feature not implemented
      
      // Handle risk profile data
      const riskProfiles = riskProfileRes.data.results || riskProfileRes.data || [];
      const currentRiskProfile = riskProfiles.length > 0 ? riskProfiles[0] : null;
      setRiskProfile(currentRiskProfile);
      
      // Fetch risk profile allocations if we have a risk profile
      if (currentRiskProfile) {
        const allocationsRes = await apiService.getRiskProfileAllocations({ 
          risk_profile: currentRiskProfile.id 
        }).catch(() => ({ data: { results: [] } }));
        setRiskAllocations(allocationsRes.data.results || allocationsRes.data || []);
      } else {
        setRiskAllocations([]);
      }

    } catch (error) {
      console.error('Error fetching user account data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculatePortfolioValue = () => {
    // Portfolio value = positions + cash (total from exchange)
    const positionsValue = livePortfolioData && connectionStatus === 'Connected' 
      ? livePortfolioData.positions.reduce((total, position) => total + (position.marketValue || 0), 0)
      : portfolios.reduce((total, position) => {
          const value = parseFloat(position.quantity || 0) * parseFloat(position.average_price || 0);
          return total + (isNaN(value) ? 0 : value);
        }, 0);
    
    const cashValue = calculateTotalCash();
    return positionsValue + cashValue;
  };

  const calculateTotalCash = () => {
    // Use live data if available and connected
    if (livePortfolioData && connectionStatus === 'Connected') {
      let totalCash = 0;
      Object.values(livePortfolioData.cash_balances).forEach(currencyData => {
        // Use TotalCashValue if available, otherwise CashBalance
        const cashValue = currencyData.TotalCashValue?.value || currencyData.CashBalance?.value || 0;
        totalCash += cashValue;
      });
      return totalCash;
    }
    
    // Fallback to database data
    return cashBalances.reduce((total, balance) => {
      const amount = parseFloat(balance.balance || 0);
      return total + (isNaN(amount) ? 0 : amount);
    }, 0);
  };

  const renderPortfolioTab = () => {
    const portfolioValue = calculatePortfolioValue(); // Total account value (positions + cash)
    const cashBalance = calculateTotalCash(); // Available cash
    const positionsValue = portfolioValue - cashBalance; // Value of current holdings

    return (
      <div className="space-y-6">
        {/* Connection Status for Broker API Accounts */}
        {supportsBrokerAPI && (
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2">
                  {connectionStatus === 'Connected' ? (
                    <Wifi className="text-green-400" size={20} />
                  ) : connectionStatus === 'Disconnected' ? (
                    <WifiOff className="text-red-400" size={20} />
                  ) : (
                    <AlertCircle className="text-yellow-400" size={20} />
                  )}
                  <div>
                    <h3 className="text-lg font-semibold text-white">Broker API Connection</h3>
                    <p className="text-gray-400 text-sm">
                      {livePortfolioData 
                        ? 'Showing current data synced from broker API'
                        : 'Data automatically syncs when page loads. Click "Resync" to refresh.'
                      }
                    </p>
                    {liveDataError && (
                      <p className="text-red-400 text-xs mt-1">
                        Error: {liveDataError}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="text-right">
                  <div className={`px-3 py-1 rounded text-sm font-medium ${getConnectionStatusColors(connectionStatus)}`}>
                    {isChecking ? 'Checking...' : connectionStatus}
                  </div>
                  {lastChecked && (
                    <p className="text-xs text-gray-400 mt-1">
                      Last checked: {lastChecked.toLocaleTimeString()}
                    </p>
                  )}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={refreshConnection}
                    disabled={isChecking}
                    className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors disabled:opacity-50"
                    title="Refresh connection status"
                  >
                    <RefreshCw className={`text-gray-300 ${isChecking ? 'animate-spin' : ''}`} size={16} />
                  </button>
                  {connectionStatus === 'Connected' && (
                    <button
                      onClick={fetchLivePortfolioData}
                      disabled={liveDataLoading}
                      className="px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 text-white text-sm"
                      title="Resync portfolio data from broker API"
                    >
                      {liveDataLoading ? 'Syncing...' : 'Resync'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Portfolio Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Total Portfolio Value</p>
                <p className="text-3xl font-bold text-white">{formatCurrency(portfolioValue)}</p>
                <p className="text-xs text-gray-500 mt-1">Account Total</p>
              </div>
              <TrendingUp className="text-green-500" size={28} />
            </div>
          </div>
          
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Cash Balance</p>
                <p className="text-2xl font-bold text-white">{formatCurrency(cashBalance)}</p>
                <p className="text-xs text-gray-500 mt-1">Available to Invest</p>
              </div>
              <CreditCard className="text-blue-500" size={24} />
            </div>
          </div>
          
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Positions Value</p>
                <p className="text-2xl font-bold text-white">{formatCurrency(positionsValue)}</p>
                <p className="text-xs text-gray-500 mt-1">Currently Invested</p>
              </div>
              <PieChart className="text-purple-500" size={24} />
            </div>
          </div>
        </div>

        {/* Holdings */}
        <div className="bg-gray-800/50 border border-gray-700 rounded-lg">
          <div className="p-6 border-b border-gray-700">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold text-white">Current Holdings</h3>
              {supportsBrokerAPI && connectionStatus !== 'Connected' && (
                <div className="flex items-center space-x-2">
                  <AlertCircle className="text-yellow-400" size={16} />
                  <span className="text-yellow-400 text-sm">Data may be outdated</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-700/50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase">Instrument</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase">Quantity</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase">Avg Price</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase">Value</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase">Allocation</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {/* Use live data if available, otherwise fall back to database data */}
                {(() => {
                  const displayData = (livePortfolioData && connectionStatus === 'Connected') 
                    ? livePortfolioData.positions 
                    : portfolios;
                  
                  return displayData.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-12 text-center">
                        <div className="text-gray-400">
                          {supportsBrokerAPI && connectionStatus === 'Connected' ? (
                            <div className="space-y-2">
                              <p>No current holdings found</p>
                              <p className="text-sm text-gray-500">Portfolio data will sync automatically when positions are opened</p>
                            </div>
                          ) : supportsBrokerAPI && connectionStatus === 'Disconnected' ? (
                            <div className="space-y-2">
                              <p>Portfolio data unavailable</p>
                              <p className="text-sm text-gray-500">Connect to IB Gateway to view current holdings</p>
                            </div>
                          ) : (
                            'No holdings found'
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    displayData.map((position, index) => {
                      // Handle both live data and database data
                      const isLiveData = livePortfolioData && connectionStatus === 'Connected';
                      
                      let symbol, quantity, avgPrice, value, allocation;
                      
                      if (isLiveData) {
                        // Live data from IB Gateway
                        symbol = position.symbol;
                        quantity = position.quantity;
                        avgPrice = position.averageCost;
                        value = position.marketValue;
                        allocation = portfolioValue > 0 ? (value / portfolioValue) * 100 : 0;
                      } else {
                        // Database data
                        symbol = position.instrument?.ticker;
                        quantity = parseFloat(position.quantity || 0);
                        avgPrice = parseFloat(position.average_price || 0);
                        value = quantity * avgPrice;
                        allocation = portfolioValue > 0 ? (value / portfolioValue) * 100 : 0;
                      }
                      
                      return (
                        <tr key={isLiveData ? `live-${index}` : position.id} className="hover:bg-gray-700/30">
                          <td className="px-6 py-4">
                            <div>
                              <div className="text-white font-medium">{symbol}</div>
                              <div className="text-xs text-gray-400">
                                {isLiveData ? `${position.secType} • ${position.exchange}` : position.instrument?.name}
                                {isLiveData && (
                                  <span className="ml-2 px-1.5 py-0.5 bg-green-500/10 text-green-400 rounded text-xs">
                                    LIVE
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-gray-300">{quantity.toFixed(4)}</td>
                          <td className="px-6 py-4 text-gray-300">
                            {formatCurrency(avgPrice)}
                            {isLiveData && position.marketPrice && (
                              <div className="text-xs text-gray-500">
                                Mkt: {formatCurrency(position.marketPrice)}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 text-gray-300">
                            {formatCurrency(value)}
                            {isLiveData && position.unrealizedPNL !== 0 && (
                              <div className={`text-xs ${position.unrealizedPNL > 0 ? 'text-green-400' : 'text-red-400'}`}>
                                {position.unrealizedPNL > 0 ? '+' : ''}{formatCurrency(position.unrealizedPNL)}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-2">
                              <div className="w-24 bg-gray-700 rounded-full h-2">
                                <div 
                                  className="bg-blue-500 h-2 rounded-full" 
                                  style={{ width: `${Math.min(allocation, 100)}%` }}
                                ></div>
                              </div>
                              <span className="text-gray-300 text-sm">{allocation.toFixed(1)}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  );
                })()}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    );
  };

  // Helper functions for allocation management
  const addAllocation = () => {
    setRiskProfileFormData(prev => ({
      ...prev,
      allocations: [
        ...prev.allocations,
        { instrument_id: '', allocation_percent: '' }
      ]
    }));
  };

  const updateAllocation = (index, field, value) => {
    setRiskProfileFormData(prev => {
      // If changing instrument, check for duplicates
      if (field === 'instrument_id' && value) {
        const isDuplicate = prev.allocations.some((alloc, i) => 
          i !== index && alloc.instrument_id === value
        );
        if (isDuplicate) {
          // Show warning and don't allow duplicate selection
          const instrument = instruments.find(inst => inst.id.toString() === value);
          setDuplicateWarning(`${instrument?.ticker} is already allocated. You can only change the percentage.`);
          setTimeout(() => setDuplicateWarning(''), 3000);
          return prev;
        }
      }
      
      return {
        ...prev,
        allocations: prev.allocations.map((alloc, i) => 
          i === index ? { ...alloc, [field]: value } : alloc
        )
      };
    });
  };

  const removeAllocation = (index) => {
    setRiskProfileFormData(prev => ({
      ...prev,
      allocations: prev.allocations.filter((_, i) => i !== index)
    }));
  };

  const calculateTotalInstrumentAllocation = () => {
    return riskProfileFormData.allocations.reduce(
      (sum, alloc) => {
        const value = alloc.allocation_percent;
        return sum + (value === '' ? 0 : parseFloat(value) || 0);
      }, 0
    );
  };

  // Group instruments by asset type/market for better UX
  const groupInstrumentsByMarket = () => {
    const groups = {};
    
    instruments.forEach(instrument => {
      // Determine asset type based on instrument characteristics
      let assetType = 'Other';
      
      if (instrument.instrument_type?.toLowerCase().includes('crypto') || 
          ['BTC', 'ETH', 'BTCL', 'ETHA', 'ETHU', 'IBIT'].some(crypto => 
            instrument.ticker?.toUpperCase().includes(crypto))) {
        assetType = 'Crypto';
      } else if (instrument.ticker?.includes('QQQ') || 
                 instrument.ticker?.includes('SPY') || 
                 instrument.ticker?.includes('DIA') ||
                 instrument.ticker?.includes('IWM') ||
                 instrument.ticker?.includes('SSO') ||
                 instrument.ticker?.includes('DDM') ||
                 instrument.ticker?.includes('QLD') ||
                 instrument.ticker?.includes('UWM')) {
        assetType = 'US Stock Indexes';
      } else if (instrument.exchange_name?.toLowerCase().includes('india') ||
                 instrument.instrument_type?.toLowerCase().includes('india')) {
        assetType = 'Indian Stock Indexes';
      } else {
        assetType = 'Other Instruments';
      }
      
      if (!groups[assetType]) {
        groups[assetType] = {};
      }
      
      const symbol = instrument.symbol || instrument.ticker?.replace(/[0-9]/g, '').replace(/[XL]/g, '');
      if (!groups[assetType][symbol]) {
        groups[assetType][symbol] = [];
      }
      
      groups[assetType][symbol].push(instrument);
    });
    
    return groups;
  };

  const generatePieChartData = () => {
    const data = [];
    const totalInstrumentAllocation = calculateTotalInstrumentAllocation();
    
    const cashReservePercent = riskProfileFormData.min_cash_reserve_percent || 0;
    const availableForInvestment = 100 - cashReservePercent;
    const unallocatedCash = availableForInvestment - totalInstrumentAllocation;
    
    // Add cash reserve
    if (cashReservePercent > 0) {
      data.push({
        name: 'Cash Reserve',
        value: cashReservePercent,
        fill: '#6B7280' // Gray for reserve
      });
    }
    
    // Add unallocated investment cash if any
    if (unallocatedCash > 0) {
      data.push({
        name: 'Unallocated Cash',
        value: unallocatedCash,
        fill: '#9CA3AF' // Light gray for unallocated investment cash
      });
    }
    
    // Add instrument allocations
    riskProfileFormData.allocations.forEach((alloc, index) => {
      const allocPercent = alloc.allocation_percent === '' ? 0 : parseFloat(alloc.allocation_percent) || 0;
      if (allocPercent > 0 && alloc.instrument_id) {
        const instrument = instruments.find(inst => inst.id.toString() === alloc.instrument_id.toString());
        data.push({
          name: instrument ? getInstrumentDisplayName(instrument) : `Instrument ${index + 1}`,
          ticker: instrument ? instrument.ticker : `Instrument ${index + 1}`,
          symbolLabel: instrument ? getInstrumentDisplayName(instrument) : null,
          fullName: instrument?.name || null,
          value: allocPercent,
          fill: `hsl(${(index * 137.5) % 360}, 70%, 50%)` // Generate colors
        });
      }
    });
    
    return data;
  };

  const handleCreateRiskProfile = async () => {
    try {
      setSubmitting(true);
      setFormErrors({});
      
      const payload = {
        customuser_id: userAccount.customuser.id,
        exchange_id: userAccount.exchange.id,
        name: riskProfileFormData.name,
        min_cash_reserve_percent: riskProfileFormData.min_cash_reserve_percent,
        allocations: riskProfileFormData.allocations.filter(alloc => {
          const allocPercent = alloc.allocation_percent === '' ? 0 : parseFloat(alloc.allocation_percent) || 0;
          return alloc.instrument_id && allocPercent > 0;
        })
      };
      
      let response;
      if (editingRiskProfile) {
        // Update existing risk profile
        response = await apiService.updateRiskProfile(editingRiskProfile.id, payload);
      } else {
        // Create new risk profile
        response = await apiService.createRiskProfile(payload);
      }
      
      setRiskProfile(response.data);
      setShowRiskProfileModal(false);
      setEditingRiskProfile(null);
      
      // Refresh data
      await fetchUserAccountData();
      
    } catch (error) {
      console.error(`Error ${editingRiskProfile ? 'updating' : 'creating'} risk profile:`, error);
      if (error.response?.data) {
        setFormErrors(error.response.data);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const openCreateModal = () => {
    setEditingRiskProfile(null);
    setRiskProfileFormData({
      name: `${userAccount?.exchange?.name} Allocation Strategy`,
      min_cash_reserve_percent: 0,
      allocations: []
    });
    setFormErrors({});
    setShowRiskProfileModal(true);
  };

  // Custom Dropdown Component
  const CustomInstrumentDropdown = ({ value, onChange, index }) => {
    const dropdownKey = `dropdown-${index}`;
    const isOpen = openDropdowns[dropdownKey];
    const groupedInstruments = groupInstrumentsByMarket();
    
    const selectedInstrument = instruments.find(inst => inst.id.toString() === value?.toString());
    
    const toggleDropdown = () => {
      setOpenDropdowns(prev => ({
        ...prev,
        [dropdownKey]: !prev[dropdownKey]
      }));
    };
    
    const selectInstrument = (instrumentId) => {
      onChange(instrumentId);
      setOpenDropdowns(prev => ({
        ...prev,
        [dropdownKey]: false
      }));
    };
    
    return (
      <div className="relative">
        <button
          type="button"
          onClick={toggleDropdown}
          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-blue-500 flex items-center justify-between"
        >
          <span className="truncate">
            {selectedInstrument ? `${selectedInstrument.ticker} - ${selectedInstrument.name}` : 'Select Instrument'}
          </span>
          <ChevronDown size={16} className={`transform transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        
        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-xl max-h-64 overflow-y-auto">
            {Object.entries(groupedInstruments).map(([assetType, symbols]) => {
              const icon = assetType === 'Crypto' ? '₿' : 
                          assetType === 'US Stock Indexes' ? '🇺🇸' : 
                          assetType === 'Indian Stock Indexes' ? '🇮🇳' : '📊';
              
              const sectionColor = assetType === 'Crypto' ? 'bg-orange-50 text-orange-800 border-orange-200' : 
                                  assetType === 'US Stock Indexes' ? 'bg-blue-50 text-blue-800 border-blue-200' : 
                                  assetType === 'Indian Stock Indexes' ? 'bg-green-50 text-green-800 border-green-200' : 
                                  'bg-gray-50 text-gray-800 border-gray-200';
              
              return (
                <div key={assetType} className="border-b border-gray-200 last:border-b-0">
                  <div className={`px-3 py-2 text-sm font-semibold border-l-4 ${sectionColor}`}>
                    {icon} {assetType}
                  </div>
                  {Object.entries(symbols).map(([symbol, instrumentList]) => (
                    instrumentList.map(instrument => (
                      <button
                        key={instrument.id}
                        type="button"
                        onClick={() => selectInstrument(instrument.id)}
                        className={`w-full px-4 py-2 text-left hover:bg-gray-50 focus:bg-blue-50 text-gray-900 flex items-center justify-between border-l-4 border-transparent hover:border-gray-300 focus:border-blue-300 ${
                          value?.toString() === instrument.id.toString() ? 'bg-blue-50 border-blue-400' : ''
                        }`}
                      >
                        <span className="truncate text-sm">
                          <span className="font-medium text-gray-900">{instrument.ticker}</span>
                          <span className="text-gray-600 ml-1">- {instrument.name}</span>
                        </span>
                        {value?.toString() === instrument.id.toString() && (
                          <Check size={16} className="text-blue-600 flex-shrink-0 ml-2" />
                        )}
                      </button>
                    ))
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const openEditModal = () => {
    setEditingRiskProfile(riskProfile);
    setRiskProfileFormData({
      name: riskProfile.name,
      min_cash_reserve_percent: riskProfile.min_cash_reserve_percent,
      allocations: riskAllocations.map(alloc => ({
        instrument_id: alloc.instrument || alloc.instrument?.id, // Use instrument ID directly
        allocation_percent: parseFloat(alloc.allocation_percent)
      }))
    });
    setFormErrors({});
    setShowRiskProfileModal(true);
  };

  const renderRiskProfileTab = () => {
    if (!riskProfile) {
      return (
        <div className="space-y-6">
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg">
            <div className="p-6 text-center">
              <Shield className="mx-auto text-gray-500 mb-4" size={48} />
              <h3 className="text-xl font-semibold text-white mb-2">No Investment Strategy</h3>
              <p className="text-gray-400 mb-6">
                Create an investment strategy for {userAccount?.exchange?.name} to define how capital should be allocated across instruments.
              </p>
              <button 
                onClick={openCreateModal}
                className="flex items-center space-x-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg mx-auto"
              >
                <Plus size={20} />
                <span>Create Investment Strategy</span>
              </button>
            </div>
          </div>
        </div>
      );
    }

    const pieData = riskAllocations.map((alloc, index) => ({
      name: alloc.instrument_ticker || alloc.instrument?.ticker || 'Unknown', // Show ticker in pie chart
      fullName: alloc.instrument_name || alloc.instrument?.name || 'Unknown Instrument',
      ticker: alloc.instrument_ticker || alloc.instrument?.ticker || 'Unknown',
      symbolLabel: alloc.symbol_name || alloc.instrument?.symbol?.label || 'Unknown',
      value: parseFloat(alloc.allocation_percent),
      fill: `hsl(${(index * 137.5) % 360}, 70%, 50%)`
    }));
    
    const totalInstrumentAllocation = riskAllocations.reduce(
      (sum, alloc) => sum + parseFloat(alloc.allocation_percent), 0
    );
    const cashReservePercent = parseFloat(riskProfile.min_cash_reserve_percent);
    const availableForInvestment = 100 - cashReservePercent;
    const unallocatedCash = availableForInvestment - totalInstrumentAllocation;

    // Add cash reserve
    if (cashReservePercent > 0) {
      pieData.push({
        name: 'Cash Reserve',
        value: cashReservePercent,
        fill: '#6B7280'
      });
    }
    
    // Add unallocated investment cash if any
    if (unallocatedCash > 0) {
      pieData.push({
        name: 'Unallocated Cash',
        value: unallocatedCash,
        fill: '#9CA3AF'
      });
    }

    return (
      <div className="space-y-6">
        {/* Investment Strategy Overview */}
        <div className="bg-gray-800/50 border border-gray-700 rounded-lg">
          <div className="p-6 border-b border-gray-700 flex items-center justify-between">
            <div>
              <h3 className="text-xl font-semibold text-white">{riskProfile.name}</h3>
              <p className="text-gray-400">Exchange: {userAccount?.exchange?.name}</p>
            </div>
            <button
              onClick={openEditModal}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg"
            >
              <Edit size={16} />
              <span>Edit</span>
            </button>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Allocation Summary */}
              <div>
                <h4 className="text-lg font-semibold text-white mb-4">Allocation Summary</h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">Cash Reserve:</span>
                    <span className="text-white font-semibold">{cashReservePercent}%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">Available for Investment:</span>
                    <span className="text-white font-semibold">{availableForInvestment.toFixed(1)}%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">Current Instrument Allocation:</span>
                    <span className="text-white font-semibold">{totalInstrumentAllocation.toFixed(1)}%</span>
                  </div>
                  <div className="border-t border-gray-600 pt-2 flex justify-between items-center">
                    <span className="text-gray-300 font-semibold">Unallocated Cash:</span>
                    <span className={`font-bold ${unallocatedCash === 0 ? 'text-green-400' : unallocatedCash < 0 ? 'text-red-400' : 'text-yellow-400'}`}>
                      {unallocatedCash.toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Pie Chart */}
              <div className="lg:col-span-2">
                <h4 className="text-lg font-semibold text-white mb-4">Allocation Visualization</h4>
                {pieData.length > 0 ? (
                  <>
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsPieChart>
                          <Pie
                            data={pieData}
                            cx="50%"
                            cy="50%"
                            outerRadius={120}
                            dataKey="value"
                            label={({ name, value, symbolLabel, ticker }) => {
                            const displayName = symbolLabel || name; // Show symbol outside (SNP, IXIC, etc.)
                            return `${displayName}: ${value}%`;
                          }}
                            labelLine={false}
                          >
                            {pieData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                            <LabelList 
                              dataKey="ticker" 
                              position="inside"
                              style={{ 
                                fill: '#f8fafc', 
                                fontSize: '12px', 
                                fontWeight: '500',
                                textAnchor: 'middle',
                                textShadow: '0px 1px 3px rgba(0,0,0,0.5)'
                              }}
                            />
                          </Pie>
                          <Tooltip 
                            formatter={(value, name, props) => {
                              const payload = props.payload;
                              
                              // Show ticker and full instrument name in tooltip
                              let displayName = payload?.ticker || 'Unknown';
                              if (payload?.fullName && payload.fullName !== 'Unknown Instrument') {
                                displayName = `${payload.ticker} - ${payload.fullName}`;
                              }
                              
                              return [`${value}%`, displayName];
                            }}
                            contentStyle={{ 
                              backgroundColor: '#374151', 
                              border: '1px solid #6B7280',
                              borderRadius: '0.5rem',
                              color: '#F3F4F6'
                            }}
                          />
                        </RechartsPieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
                      {pieData.map((entry, index) => (
                        <div key={index} className="flex items-center space-x-3 p-3 bg-gray-700/30 rounded-lg">
                          <div 
                            className="w-4 h-4 rounded-full flex-shrink-0"
                            style={{ backgroundColor: entry.fill }}
                          ></div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="text-white font-semibold text-sm">
                                  {entry.symbolLabel || entry.name}
                                </div>
                                {entry.symbolLabel && entry.ticker && (
                                  <div className="text-xs text-gray-400">
                                    {entry.ticker}
                                  </div>
                                )}
                              </div>
                              <div className="text-white font-bold">
                                {entry.value}%
                              </div>
                            </div>
                            {entry.fullName && entry.fullName !== 'Unknown Instrument' && (
                              <div className="text-xs text-gray-400 truncate">
                                {entry.fullName}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="h-64 flex items-center justify-center text-gray-400">
                    No allocations defined
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

      </div>
    );
  };

  const renderTransactionsTab = () => {
    return (
      <div className="bg-gray-800/50 border border-gray-700 rounded-lg">
        <div className="p-6 border-b border-gray-700">
          <h3 className="text-xl font-semibold text-white">Trade History</h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-700/50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase">Date</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase">Instrument</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase">Type</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase">Quantity</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase">Price</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase">Total</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {trades.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-400">
                    No trades available
                    <div className="text-xs mt-2 text-gray-500">
                      Trading history will appear here once implemented
                    </div>
                  </td>
                </tr>
              ) : (
                trades.map((trade) => (
                  <tr key={trade.id} className="hover:bg-gray-700/30">
                    <td className="px-6 py-4 text-gray-300">
                      {new Date(trade.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-white font-medium">{trade.instrument?.ticker}</div>
                      <div className="text-xs text-gray-400">{trade.instrument?.name}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${
                        trade.trade_type === 'buy' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'
                      }`}>
                        {trade.trade_type?.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-300">{parseFloat(trade.quantity || 0).toFixed(4)}</td>
                    <td className="px-6 py-4 text-gray-300">{formatCurrency(parseFloat(trade.price || 0))}</td>
                    <td className="px-6 py-4 text-gray-300">{formatCurrency(parseFloat(trade.total_value || 0))}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${
                        trade.status === 'filled' ? 'bg-green-500/10 text-green-400' :
                        trade.status === 'pending' ? 'bg-yellow-500/10 text-yellow-400' :
                        'bg-red-500/10 text-red-400'
                      }`}>
                        {trade.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };


  const renderTabContent = () => {
    switch (activeTab) {
      case 'risk-profile':
        return renderRiskProfileTab();
      case 'portfolio':
        return renderPortfolioTab();
      case 'transactions':
        return renderTransactionsTab();
      default:
        return renderRiskProfileTab();
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="animate-spin text-blue-500" size={32} />
        </div>
      </Layout>
    );
  }

  if (!userAccount) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-gray-400">User account not found</p>
          <button 
            onClick={() => navigate('/connected-user-accounts')}
            className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
          >
            Back to Connected User Accounts
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/connected-user-accounts')}
              className="p-2 text-gray-400 hover:text-white"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-white">Connected Account Detail</h1>
              <p className="text-gray-400">
              {userAccount.customuser?.email} - {userAccount.exchange?.name}
            </p>
            </div>
          </div>
          
          {/* Prominent Mode Indicator */}
          <div className={`px-4 py-2 rounded-lg border-2 flex items-center space-x-3 ${
            userAccount.mode === 'live'
              ? 'border-red-500 bg-red-500/10 text-red-400'
              : 'border-yellow-500 bg-yellow-500/10 text-yellow-400'
          }`}>
            <div className={`w-3 h-3 rounded-full ${
              userAccount.mode === 'live' ? 'bg-red-400' : 'bg-yellow-400'
            }`}></div>
            <div>
              <div className="font-semibold text-sm">
                {userAccount.mode === 'live' ? 'LIVE TRADING' : 'PAPER TRADING'}
              </div>
              <div className="text-xs opacity-75">
                {userAccount.mode === 'live' ? 'Real money' : 'Simulation'}
              </div>
            </div>
          </div>
        </div>

        {/* User Info Card */}
        <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
              <User size={24} className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">
                {userAccount.customuser?.first_name} {userAccount.customuser?.last_name}
              </h2>
              <p className="text-gray-400">{userAccount.customuser?.email}</p>
              <div className="flex items-center space-x-4 mt-2">
                <span className="text-sm text-gray-300">
                  Exchange: {userAccount.exchange?.name}
                </span>
                <span className={`px-2 py-1 rounded text-xs font-medium flex items-center space-x-1 w-fit ${
                  userAccount.mode === 'live'
                    ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                    : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                }`}>
                  <div className={`w-2 h-2 rounded-full ${
                    userAccount.mode === 'live' ? 'bg-red-400' : 'bg-yellow-400'
                  }`}></div>
                  <span>{userAccount.mode} mode</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-8 border-b border-gray-700">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-500'
                  : 'border-transparent text-gray-400 hover:text-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <tab.icon size={16} />
                <span>{tab.label}</span>
              </div>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {renderTabContent()}

        {/* Investment Strategy Creation Modal */}
        {showRiskProfileModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-gray-800 border border-gray-700 rounded-lg w-full max-w-4xl mx-4 max-h-screen overflow-y-auto">
              <div className="p-6 border-b border-gray-700">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-white">
                    {editingRiskProfile ? 'Edit Investment Strategy' : 'Create Investment Strategy'}
                  </h2>
                  <button
                    onClick={() => setShowRiskProfileModal(false)}
                    className="text-gray-400 hover:text-white"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Strategy Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Strategy Name
                  </label>
                  <input
                    type="text"
                    value={riskProfileFormData.name}
                    onChange={(e) => setRiskProfileFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                    placeholder="Enter strategy name"
                  />
                  {formErrors.name && (
                    <p className="text-red-400 text-sm mt-1">{formErrors.name}</p>
                  )}
                </div>

                {/* Cash Reserve */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Minimum Cash Reserve (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={riskProfileFormData.min_cash_reserve_percent}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      setRiskProfileFormData(prev => ({ 
                        ...prev, 
                        min_cash_reserve_percent: value
                      }));
                    }}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                    placeholder="Enter percentage (e.g., 20)"
                  />
                  <p className="text-gray-400 text-xs mt-1">
                    Minimum percentage of portfolio to keep as cash reserve. Remaining percentage can be invested.
                  </p>
                  <p className="text-blue-400 text-xs">
                    Example: 20% means always keep at least 20% cash, invest up to 80% in instruments.
                  </p>
                  <p className="text-green-400 text-xs">
                    Default: 0% = Always fully invested (100% in instruments when opportunities arise).
                  </p>
                  {formErrors.min_cash_reserve_percent && (
                    <p className="text-red-400 text-sm mt-1">{formErrors.min_cash_reserve_percent}</p>
                  )}
                </div>

                {/* Instrument Allocations */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-300">
                      Instrument Allocations
                    </label>
                    <button
                      onClick={addAllocation}
                      disabled={calculateTotalInstrumentAllocation() >= (100 - riskProfileFormData.min_cash_reserve_percent)}
                      className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                        calculateTotalInstrumentAllocation() >= (100 - riskProfileFormData.min_cash_reserve_percent)
                          ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                          : 'bg-blue-600 hover:bg-blue-700 text-white'
                      }`}
                      title={calculateTotalInstrumentAllocation() >= (100 - riskProfileFormData.min_cash_reserve_percent) ? `Cannot add more instruments - ${100 - riskProfileFormData.min_cash_reserve_percent}% max allowed` : 'Add new instrument allocation'}
                    >
                      <Plus size={16} />
                      <span>Add Instrument</span>
                    </button>
                  </div>
                  <p className="text-gray-400 text-xs mb-4">
                    Define how to allocate the deployed cash among instruments. These percentages are relative to the deployed amount, not total portfolio.
                  </p>
                  
                  {/* Duplicate Warning */}
                  {duplicateWarning && (
                    <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                      <p className="text-yellow-400 text-sm">{duplicateWarning}</p>
                    </div>
                  )}

                  <div className="space-y-3">
                    {riskProfileFormData.allocations.map((allocation, index) => (
                      <div key={index} className="flex items-center space-x-3 p-3 bg-gray-700/30 rounded-lg">
                        <div className="flex-1">
                          <CustomInstrumentDropdown
                            value={allocation.instrument_id}
                            onChange={(instrumentId) => updateAllocation(index, 'instrument_id', instrumentId)}
                            index={index}
                          />
                        </div>
                        <div className="w-32">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.1"
                            placeholder="0.0"
                            value={allocation.allocation_percent || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              updateAllocation(index, 'allocation_percent', value === '' ? '' : parseFloat(value) || 0);
                            }}
                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-blue-500"
                          />
                        </div>
                        <div className="text-gray-400 text-sm">%</div>
                        <button
                          onClick={() => removeAllocation(index)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Allocation Summary */}
                <div className="bg-gray-700/50 rounded-lg p-4">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Minimum Cash Reserve:</span>
                      <span className="font-bold text-blue-400">
                        {riskProfileFormData.min_cash_reserve_percent}%
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Maximum Investment Capacity:</span>
                      <span className="font-bold text-purple-400">
                        {100 - riskProfileFormData.min_cash_reserve_percent}%
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Current Instrument Allocation:</span>
                      <span className={`font-bold text-lg ${
                        calculateTotalInstrumentAllocation() === (100 - riskProfileFormData.min_cash_reserve_percent) ? 'text-green-400' : 
                        calculateTotalInstrumentAllocation() > (100 - riskProfileFormData.min_cash_reserve_percent) ? 'text-red-400' : 'text-yellow-400'
                      }`}>
                        {calculateTotalInstrumentAllocation().toFixed(1)}%
                      </span>
                    </div>
                  </div>
                  {calculateTotalInstrumentAllocation() > (100 - riskProfileFormData.min_cash_reserve_percent) && (
                    <p className="text-red-400 text-sm mt-2">
                      Instrument allocations cannot exceed {100 - riskProfileFormData.min_cash_reserve_percent}% (due to {riskProfileFormData.min_cash_reserve_percent}% cash reserve)
                    </p>
                  )}
                  {calculateTotalInstrumentAllocation() < (100 - riskProfileFormData.min_cash_reserve_percent) && calculateTotalInstrumentAllocation() > 0 && (
                    <p className="text-yellow-400 text-sm mt-2">
                      Available for allocation: {((100 - riskProfileFormData.min_cash_reserve_percent) - calculateTotalInstrumentAllocation()).toFixed(1)}%
                    </p>
                  )}
                  <div className="mt-3 p-3 bg-gray-600/30 rounded border-l-4 border-green-500">
                    <p className="text-green-400 text-sm font-medium">Strategy Example:</p>
                    <p className="text-gray-300 text-xs mt-1">
                      With {riskProfileFormData.min_cash_reserve_percent}% cash reserve, 
                      if you have $10,000 portfolio, you'll keep at least ${(riskProfileFormData.min_cash_reserve_percent * 100).toLocaleString()} as cash
                      and can invest up to ${((100 - riskProfileFormData.min_cash_reserve_percent) * 100).toLocaleString()} in instruments.
                    </p>
                  </div>
                </div>

                {/* Live Pie Chart Preview */}
                {generatePieChartData().length > 0 && (
                  <div className="bg-gray-700/50 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-white mb-4">Portfolio Strategy Preview</h3>
                    <p className="text-gray-400 text-xs mb-4">Shows how your total portfolio will be structured based on cash deployment and instrument allocations.</p>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsPieChart>
                          <Pie
                            data={generatePieChartData()}
                            cx="50%"
                            cy="50%"
                            outerRadius={120}
                            dataKey="value"
                            label={({ name, value, symbolLabel, ticker }) => {
                            const displayName = symbolLabel || name; // Show symbol outside (SNP, IXIC, etc.)
                            return `${displayName}: ${value}%`;
                          }}
                            labelLine={false}
                          >
                            {generatePieChartData().map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                            <LabelList 
                              dataKey="ticker" 
                              position="inside"
                              style={{ 
                                fill: '#f8fafc', 
                                fontSize: '12px', 
                                fontWeight: '500',
                                textAnchor: 'middle',
                                textShadow: '0px 1px 3px rgba(0,0,0,0.5)'
                              }}
                            />
                          </Pie>
                          <Tooltip 
                            formatter={(value, name, props) => {
                              const payload = props.payload;
                              
                              // Show ticker and full instrument name in tooltip
                              let displayName = payload?.ticker || 'Unknown';
                              if (payload?.fullName && payload.fullName !== 'Unknown Instrument') {
                                displayName = `${payload.ticker} - ${payload.fullName}`;
                              }
                              
                              return [`${value}%`, displayName];
                            }}
                            contentStyle={{ 
                              backgroundColor: '#374151', 
                              border: '1px solid #6B7280',
                              borderRadius: '0.5rem',
                              color: '#F3F4F6'
                            }}
                          />
                          <Legend />
                        </RechartsPieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                {/* Error Display */}
                {formErrors.non_field_errors && (
                  <div className="text-red-400 text-sm">
                    {formErrors.non_field_errors.map((error, index) => (
                      <p key={index}>{error}</p>
                    ))}
                  </div>
                )}
              </div>

              <div className="p-6 border-t border-gray-700 flex justify-end space-x-3">
                <button
                  onClick={() => setShowRiskProfileModal(false)}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateRiskProfile}
                  disabled={
                    submitting || 
                    calculateTotalInstrumentAllocation() > (100 - riskProfileFormData.min_cash_reserve_percent) || 
                    !riskProfileFormData.name.trim() ||
                    riskProfileFormData.min_cash_reserve_percent < 0 ||
                    riskProfileFormData.min_cash_reserve_percent > 100
                  }
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg flex items-center space-x-2"
                >
                  {submitting && <RefreshCw size={16} className="animate-spin" />}
                  <span>
                    {submitting 
                      ? (editingRiskProfile ? 'Updating...' : 'Creating...')
                      : (editingRiskProfile ? 'Update Profile' : 'Create Profile')
                    }
                  </span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default UserAccountDetail;